import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const PRODUCT_ITEMS = [
  { to: '/product/total-protection', icon: '◉', label: 'Total Protection Suite', desc: 'Block invalid traffic across every paid and organic channel from one dashboard.' },
  { to: '/product/ppc-shield',       icon: '⛉', label: 'PPC Shield',             desc: 'Real-time click-fraud blocking for Google Ads, Meta, and Microsoft Ads.' },
  { to: '/product/bot-mitigation',   icon: '◍', label: 'Bot Mitigation',         desc: 'Stop scrapers, crawlers and automation that never touched an ad.' },
  { to: '/product/lead-shield',      icon: '⤶', label: 'Lead Shield', badge: 'New', desc: 'Scores every form submission and blocks fake leads before they reach your CRM.' },
];

const RESOURCE_ITEMS = [
  { to: '/blog',             icon: '▤', label: 'Blog',             desc: 'Read articles and guides by our expert content team.' },
  { to: '/case-studies',     icon: '◱', label: 'Case Studies',     desc: 'Real-life examples of ClickSaver in action from genuine customers.' },
  { to: '/faq',              icon: '◉', label: 'FAQ',              desc: 'Got questions about using ClickSaver? We answer all the common ones.' },
  { to: '/knowledge-center', icon: '◈', label: 'Knowledge Center', desc: 'Need help getting set up? Find all the documentation, webinars and info you need.' },
  { to: '/press',            icon: '▦', label: 'Press',            desc: 'ClickSaver in the news? Read our press releases and articles.' },
  { to: '/research',         icon: '◎', label: 'Research',         desc: 'Our team offers insights and findings from the world of marketing and fraud prevention.' },
  { to: '/reviews',          icon: '★', label: 'Reviews',          desc: 'What do customers think of ClickSaver? Read our reviews.' },
  { to: '/why-us',           icon: '✦', label: 'Why us?',          desc: 'Discover why ClickSaver is the industry-leading choice for businesses around the world.' },
];

const CSS = `
  .ag-root { font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #ffffff; color: #0f172a; min-height: 100vh; }
  .ag-nav { position: sticky; top: 0; background: rgba(255,255,255,0.95); backdrop-filter: blur(8px); border-bottom: 1px solid #e5e7eb; z-index: 50; }
  .ag-nav-inner { max-width: 1200px; margin: 0 auto; padding: 14px 24px; display: flex; align-items: center; justify-content: space-between; gap: 24px; }
  .ag-nav-links { display: flex; gap: 28px; font-size: 14px; font-weight: 500; }
  .ag-nav-a { color: #475569; text-decoration: none; transition: color .15s; }
  .ag-nav-a:hover { color: #0969da; }
  .ag-logo { display: flex; align-items: center; gap: 8px; text-decoration: none; }
  .ag-logo-mark { width: 32px; height: 32px; border-radius: 8px; background: linear-gradient(135deg,#0969da,#08a86b); display:flex; align-items:center; justify-content:center; color: #fff; }
  .ag-logo-text { font-size: 17px; font-weight: 700; color: #0f172a; letter-spacing: -0.015em; }
  .ag-btn { display: inline-flex; align-items: center; justify-content: center; padding: 9px 18px; border-radius: 999px; font-size: 14px; font-weight: 500; text-decoration: none; border: none; cursor: pointer; transition: background .15s, color .15s, border-color .15s, transform .15s; }
  .ag-btn-outline { border: 1px solid #cbd5e1; background: #fff; color: #0f172a; }
  .ag-btn-outline:hover { border-color: #0969da; color: #0969da; }
  .ag-btn-primary { background: #0969da; color: #fff; }
  .ag-btn-primary:hover { background: #085bbf; transform: translateY(-1px); }
  .ag-btn-orange { background: #0969da; color: #fff; }
  .ag-btn-orange:hover { background: #085bbf; transform: translateY(-1px); }

  /* Resources mega-dropdown */
  .ag-dropdown { position: relative; }
  .ag-dropdown-btn { display: inline-flex; align-items: center; gap: 4px; background: none; border: none; cursor: pointer; font: inherit; font-size: 14px; font-weight: 500; color: #475569; padding: 0; transition: color .15s; }
  .ag-dropdown-btn:hover { color: #0969da; }
  .ag-dropdown-btn.open { color: #0969da; }
  .ag-dropdown-arrow { font-size: 10px; transition: transform .15s; }
  .ag-dropdown-arrow.open { transform: rotate(180deg); }
  .ag-mega { position: absolute; top: 100%; left: 50%; transform: translateX(-50%); background: #fff; border: 1px solid #e5e7eb; border-radius: 20px; box-shadow: 0 20px 60px -20px rgba(15,23,42,0.18); padding: 24px; width: 980px; max-width: calc(100vw - 32px); z-index: 60; margin-top: 14px; }
  .ag-mega::before { content: ''; position: absolute; left: 0; right: 0; top: -16px; height: 16px; background: transparent; }
  .ag-mega-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 2px; }
  .ag-mega-item { display: flex; gap: 12px; padding: 16px 14px; border-radius: 12px; text-decoration: none; color: inherit; position: relative; transition: background .12s; }
  .ag-mega-item:not(:nth-child(4n))::after { content: ''; position: absolute; right: 0; top: 20%; bottom: 20%; width: 1px; background: #f1f5f9; }
  .ag-mega-item:hover { background: #f8fafc; }
  .ag-mega-item:hover::after { opacity: 0; }
  .ag-mega-icon { width: 32px; height: 32px; border-radius: 8px; background: #e6f0fb; color: #0969da; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 15px; }
  .ag-mega-label { font-size: 15px; font-weight: 600; color: #0f172a; margin: 0 0 4px; display: inline-flex; align-items: center; gap: 8px; }
  .ag-mega-badge { font-size: 10px; font-weight: 600; letter-spacing: 0.02em; background: #08a86b; color: #fff; padding: 2px 8px; border-radius: 999px; }
  .ag-mega-desc { font-size: 12px; color: #64748b; line-height: 1.5; margin: 0; }
  @media (max-width: 820px) {
    .ag-mega { width: calc(100vw - 32px); }
    .ag-mega-grid { grid-template-columns: 1fr 1fr; }
    .ag-mega-item::after { display: none !important; }
  }

  .ag-section { max-width: 1120px; margin: 0 auto; padding: 60px 24px; }
  .ag-section-tight { max-width: 1120px; margin: 0 auto; padding: 40px 24px; }

  /* Hero */
  .ag-hero { background: linear-gradient(180deg, #f0f7ff 0%, #ffffff 100%); padding: 60px 24px 48px; }
  .ag-hero-inner { max-width: 920px; margin: 0 auto; text-align: center; }
  .ag-pill-badge { display: inline-flex; align-items: center; padding: 6px 16px; border: 1px solid #c7deef; background: #fff; border-radius: 999px; font-size: 13px; font-weight: 500; color: #0f172a; margin-bottom: 24px; }
  .ag-h1 { font-size: 54px; font-weight: 700; letter-spacing: -0.03em; line-height: 1.1; margin: 0 0 20px; }
  .ag-h1 .accent { color: #08a86b; }
  .ag-h1 .accent-orange { color: #0969da; }
  .ag-hero-sub { max-width: 720px; margin: 0 auto 32px; font-size: 17px; color: #475569; line-height: 1.6; }
  .ag-hero-trust { display: flex; gap: 36px; justify-content: center; flex-wrap: wrap; margin-top: 36px; font-size: 14px; color: #475569; }
  .ag-trust-item { display: inline-flex; align-items: center; gap: 7px; }
  .ag-trust-icon { color: #0969da; display: inline-flex; }
  .ag-trial-note { font-size: 13px; color: #64748b; margin-top: 10px; }

  /* Section headings */
  .ag-section-h { font-size: 36px; font-weight: 700; letter-spacing: -0.025em; text-align: center; line-height: 1.2; margin: 0 0 16px; }
  .ag-section-h .accent-pink { color: #08a86b; }
  .ag-section-h .accent-orange { color: #0969da; }
  .ag-section-sub { text-align: center; max-width: 720px; margin: 0 auto 40px; color: #475569; font-size: 16px; line-height: 1.6; }

  /* "What ad platforms report" split */
  .ag-split { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 56px; align-items: center; }
  .ag-split h2 { font-size: 40px; font-weight: 700; letter-spacing: -0.025em; line-height: 1.15; margin: 0 0 24px; }
  .ag-split h2 .accent { color: #08a86b; }
  .ag-split p { font-size: 15px; color: #475569; line-height: 1.7; margin: 0 0 16px; }
  .ag-threat-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 20px; padding: 28px; position: relative; }
  .ag-threat-h { font-size: 17px; font-weight: 600; margin: 0 0 16px; }
  .ag-donut { position: relative; display: flex; justify-content: center; align-items: center; height: 180px; margin-bottom: 20px; }
  .ag-donut-overlay { position: absolute; right: 0; top: 30px; background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px 16px; box-shadow: 0 10px 30px -10px rgba(0,0,0,0.1); }
  .ag-donut-overlay-label { font-size: 12px; color: #64748b; }
  .ag-donut-overlay-value { font-size: 20px; font-weight: 600; margin-top: 2px; display: flex; align-items: baseline; gap: 6px; }
  .ag-donut-trend { font-size: 11px; color: #10b981; }
  .ag-threat-legend { display: flex; flex-direction: column; gap: 8px; font-size: 13px; }
  .ag-threat-legend-row { display: flex; align-items: center; gap: 8px; }
  .ag-threat-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
  .ag-threat-callout { background: #f0f7ff; border-radius: 16px; padding: 18px 24px; max-width: 820px; margin: 32px auto 0; font-size: 14px; color: #0f172a; line-height: 1.6; text-align: center; }

  /* "What you get" grid */
  .ag-feat-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  .ag-feat { padding: 24px; border: 1px solid #e5e7eb; border-radius: 16px; background: #fff; transition: transform .2s, box-shadow .2s; }
  .ag-feat:hover { transform: translateY(-2px); box-shadow: 0 20px 40px -16px rgba(15,23,42,0.1); }
  .ag-feat-icon { width: 40px; height: 40px; border-radius: 10px; background: #e6f0fb; color: #0969da; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 14px; }
  .ag-feat-title { font-size: 16px; font-weight: 600; margin: 0 0 6px; }
  .ag-feat-body { font-size: 14px; color: #475569; line-height: 1.6; margin: 0; }

  /* From click to block */
  .ag-process { background: linear-gradient(180deg, #f0f7ff 0%, #ffffff 100%); }
  .ag-process-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; position: relative; margin-top: 48px; }
  .ag-process-steps::before { content: ''; position: absolute; top: 20px; left: 10%; right: 10%; height: 1px; background: #e5e7eb; z-index: 0; }
  .ag-step { position: relative; z-index: 1; }
  .ag-step-num { width: 40px; height: 40px; border-radius: 50%; background: #0969da; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 16px; font-weight: 600; margin-bottom: 16px; }
  .ag-step-h { font-size: 15px; font-weight: 600; margin: 0 0 8px; line-height: 1.3; }
  .ag-step-p { font-size: 13px; color: #475569; line-height: 1.6; margin: 0; }
  .ag-process-emphasis { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 16px 24px; margin-top: 36px; font-size: 14px; color: #0f172a; }
  .ag-process-emphasis .accent { color: #0969da; }

  /* Setup */
  .ag-setup { background: #f0f7ff; }
  .ag-setup-split { display: grid; grid-template-columns: 1fr 1fr; gap: 48px; align-items: center; }
  .ag-setup h2 { font-size: 36px; font-weight: 700; letter-spacing: -0.025em; line-height: 1.15; margin: 0 0 20px; }
  .ag-setup p { font-size: 15px; color: #475569; line-height: 1.7; margin: 0 0 14px; }
  .ag-code { background: #1a1a1a; border-radius: 14px; overflow: hidden; }
  .ag-code-header { background: #262626; padding: 10px 14px; display: flex; align-items: center; gap: 7px; }
  .ag-code-dot { width: 10px; height: 10px; border-radius: 50%; }
  .ag-code-file { color: #9ca3af; font-size: 12px; margin-left: auto; }
  .ag-code-body { padding: 20px; font-family: ui-monospace, 'JetBrains Mono', monospace; font-size: 13px; line-height: 1.7; color: #e5e7eb; }
  .ag-code-comment { color: #6b7280; }
  .ag-code-tag { color: #0969da; }
  .ag-code-attr { color: #c084fc; }
  .ag-code-str { color: #86efac; }
  .ag-code-ok { margin-top: 14px; color: #4ade80; font-size: 13px; display: flex; align-items: center; gap: 8px; }
  .ag-pill-row { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 20px; }
  .ag-pill-chip { display: inline-flex; align-items: center; gap: 7px; padding: 7px 14px; border: 1px solid #e5e7eb; background: #fff; border-radius: 999px; font-size: 13px; color: #0f172a; }
  .ag-pill-chip-icon { width: 24px; height: 24px; border-radius: 50%; background: #e6f0fb; color: #0969da; display: inline-flex; align-items: center; justify-content: center; font-size: 12px; }

  /* Pricing */
  .ag-pricing-h { font-size: 36px; font-weight: 700; letter-spacing: -0.025em; text-align: center; line-height: 1.2; margin: 0 0 20px; }
  .ag-pricing-h .accent { color: #0969da; }
  .ag-entry { max-width: 920px; margin: 32px auto 36px; border: 1px solid #e5e7eb; border-radius: 20px; padding: 28px 36px; background: #ffffff; }
  .ag-entry-text { font-size: 15px; color: #475569; line-height: 1.65; text-align: center; }
  .ag-entry-text strong { color: #0f172a; font-weight: 600; }
  .ag-entry-text .strike { color: #94a3b8; text-decoration: line-through; margin-right: 6px; }
  .ag-tiers { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; max-width: 1000px; margin: 0 auto; }
  .ag-tier { border: 1px solid #e5e7eb; border-radius: 20px; padding: 28px; background: #ffffff; display: flex; flex-direction: column; min-height: 260px; transition: transform .2s, box-shadow .2s, border-color .2s; }
  .ag-tier:hover { transform: translateY(-4px); box-shadow: 0 20px 40px -16px rgba(15,23,42,0.12); border-color: #cbd5e1; }
  .ag-tier-pill { display: inline-flex; align-items: center; padding: 7px 18px; border-radius: 999px; font-size: 14px; font-weight: 600; color: #fff; align-self: flex-start; }
  .ag-tier-pill.t1 { background: #0969da; }
  .ag-tier-pill.t2 { background: #08a86b; }
  .ag-tier-pill.t3 { background: #0a3d6e; }
  .ag-tier-sub { font-size: 14px; color: #64748b; margin-top: 14px; }
  .ag-tier-pct { font-size: 72px; font-weight: 700; letter-spacing: -0.03em; margin: auto 0 0; line-height: 1; }
  .ag-tier-pct.t1 { color: #0969da; }
  .ag-tier-pct.t2 { color: #08a86b; }
  .ag-tier-pct.t3 { color: #0a3d6e; }
  .ag-tier-off { font-size: 18px; font-weight: 500; color: #0f172a; margin-top: 10px; }
  .ag-tier-ask { font-size: 22px; font-weight: 600; letter-spacing: -0.015em; margin: auto 0 10px; line-height: 1.2; }
  .ag-tier-ask.t1 { color: #0969da; }
  .ag-tier-ask.t2 { color: #08a86b; }
  .ag-tier-ask.t3 { color: #0a3d6e; }
  .ag-tier-cta { font-size: 14px; font-weight: 500; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; transition: gap .15s; }
  .ag-tier-cta.t1 { color: #0969da; }
  .ag-tier-cta.t2 { color: #08a86b; }
  .ag-tier-cta.t3 { color: #0a3d6e; }
  .ag-tier-cta:hover { gap: 8px; }
  .ag-footnote { max-width: 1000px; margin: 18px auto 0; display: flex; gap: 8px; font-size: 13px; color: #64748b; }
  .ag-footnote-dot { color: #0969da; }
  .ag-callout { max-width: 1000px; margin: 32px auto 0; background: #eaf5ff; border: 1px solid #c7deef; border-radius: 16px; padding: 22px 28px; }
  .ag-callout-h { font-size: 17px; font-weight: 600; color: #0f172a; margin: 0 0 6px; }
  .ag-callout-p { font-size: 14px; color: #475569; line-height: 1.6; margin: 0; }

  /* Getting started */
  .ag-start { background: #f0f7ff; }
  .ag-start-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; max-width: 1000px; margin: 0 auto; }
  .ag-start-card { background: #fff; border-radius: 20px; padding: 32px 24px; text-align: center; }
  .ag-start-num { width: 54px; height: 54px; border-radius: 50%; border: 2px solid #0969da; color: #0969da; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: 600; margin: 0 auto 20px; }
  .ag-start-h { font-size: 16px; font-weight: 600; margin: 0 0 10px; line-height: 1.3; }
  .ag-start-p { font-size: 13px; color: #475569; line-height: 1.6; margin: 0; }
  .ag-start-qualify { max-width: 1000px; margin: 24px auto 0; background: #f0f7ff; border: 1px solid #c7deef; border-radius: 14px; padding: 20px 28px; }
  .ag-start-qualify-h { font-size: 15px; font-weight: 600; margin: 0 0 6px; }
  .ag-start-qualify-p { font-size: 13px; color: #475569; line-height: 1.6; margin: 0; }

  /* Certified */
  .ag-cert-split { display: grid; grid-template-columns: 1.3fr 0.7fr; gap: 48px; align-items: center; }
  .ag-cert h2 { font-size: 34px; font-weight: 700; letter-spacing: -0.025em; line-height: 1.2; margin: 0 0 20px; }
  .ag-cert p { font-size: 14px; color: #475569; line-height: 1.7; margin: 0 0 12px; }
  .ag-cert-badge { background: #fff; border-radius: 20px; padding: 32px 24px; text-align: center; box-shadow: 0 10px 40px -20px rgba(15,23,42,0.15); }
  .ag-cert-ribbon { width: 76px; height: 76px; border-radius: 50%; background: #e6f0fb; color: #0969da; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; }
  .ag-cert-badge-h { font-size: 15px; font-weight: 600; line-height: 1.3; margin: 0 0 12px; }
  .ag-cert-badge-tier { display: inline-flex; align-items: center; padding: 6px 16px; border: 1px solid #0969da; color: #0969da; border-radius: 999px; font-size: 13px; font-weight: 500; }

  /* FAQ */
  .ag-faq { background: #f0f7ff; }
  .ag-faq-h { font-size: 36px; font-weight: 700; letter-spacing: -0.025em; text-align: center; line-height: 1.2; margin: 0 0 40px; }
  .ag-faq-list { max-width: 820px; margin: 0 auto; display: flex; flex-direction: column; gap: 12px; }
  .ag-faq-item { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; overflow: hidden; }
  .ag-faq-q { width: 100%; display: flex; justify-content: space-between; align-items: center; padding: 16px 22px; background: none; border: none; cursor: pointer; font-size: 15px; font-weight: 600; color: #0f172a; text-align: left; font-family: inherit; }
  .ag-faq-plus { color: #0969da; font-size: 20px; font-weight: 400; transition: transform .15s; }
  .ag-faq-plus.open { transform: rotate(45deg); }
  .ag-faq-a { padding: 0 22px 18px; font-size: 14px; color: #475569; line-height: 1.7; }

  /* CTA */
  .ag-cta { text-align: center; max-width: 820px; margin: 0 auto; padding: 60px 24px; }
  .ag-cta-h { font-size: 32px; font-weight: 700; letter-spacing: -0.015em; margin: 0 0 10px; }
  .ag-cta-p { font-size: 15px; color: #475569; margin: 0 0 24px; }
  .ag-cta-row { display: inline-flex; gap: 12px; flex-wrap: wrap; justify-content: center; }

  @media (max-width: 820px) {
    .ag-h1 { font-size: 36px; }
    .ag-section-h, .ag-pricing-h, .ag-faq-h { font-size: 28px; }
    .ag-split, .ag-setup-split, .ag-cert-split { grid-template-columns: 1fr; gap: 32px; }
    .ag-feat-grid { grid-template-columns: 1fr; }
    .ag-tiers { grid-template-columns: 1fr; }
    .ag-process-steps, .ag-start-steps { grid-template-columns: 1fr 1fr; }
    .ag-process-steps::before { display: none; }
    .ag-nav-links { display: none; }
    .ag-section, .ag-section-tight { padding: 40px 20px; }
    .ag-hero { padding: 40px 20px; }
  }
`;

const FAQ = [
  { q: 'What counts as an agency?', a: 'Any team managing paid ad accounts on behalf of five or more clients. You can be in-house at a holding company or an independent shop — if you run multiple accounts, you qualify.' },
  { q: 'What counts as a client?', a: 'A distinct business with its own domain and its own ad accounts. Multiple domains for the same business still count as one client.' },
  { q: 'One of my clients already uses a competitor. Does it count?', a: 'Yes. New or migrated accounts both count toward your tier. We handle the data migration for you when you move an existing contract across.' },
  { q: 'What does my client have to do?', a: 'Install a single tracking script. We handle Google Ads, Meta, and Microsoft Ads integrations under your partner account — the client sees nothing new.' },
  { q: 'How do I get my discount?', a: 'Partner pricing is applied automatically at billing based on the number of active clients under your partner account. No codes, no manual steps.' },
  { q: 'When does my rate change?', a: 'Rates are recalculated at the start of each billing month. If you move up a tier mid-month, the new rate applies from the next invoice.' },
  { q: 'What if I buy an annual plan?', a: 'Annual plans get the partner discount on top of the standard 20% annual saving. You lock the rate for the full year.' },
];

const FEATURES = [
  { icon: '▦', title: 'Campaigns breakdown', body: 'Every campaign by platform, threat group, invalid visits and invalid rate — the view you take into a client call.' },
  { icon: '⛉', title: 'PMax and YouTube protection', body: 'IP blocking on the campaign types where you have the least amount of manual control.' },
  { icon: '◉', title: 'Audience exclusion', body: 'Applied across Google, Meta and Microsoft Ads from the same verdict.' },
  { icon: '◍', title: 'Bot mitigation beyond the click', body: 'Scrapers, crawlers and automation tools that arrive directly, never touch an ad, and still corrupt the analytics you report on.' },
  { icon: '⤶', title: 'Lead Shield for forms', body: 'Every form submission gets a fraud verdict and a risk score, sent to your CRM so your client\'s sales team follows up on contacts that are real.' },
  { icon: '▤', title: 'White-label reporting', body: 'Client reports go out with your branding, not ours. PDF, CSV, or scheduled email.' },
  { icon: '◐', title: 'Agency permissions and account overview', body: 'Per-client access for your team, and every account\'s protection status in one view.' },
  { icon: '◎', title: 'Multiple clients\' support', body: 'All of a client\'s domains sit under your one account, with no Google Ads account limitations.' },
  { icon: '◆', title: 'Pixel Guard', body: 'Prevents your Google tags from firing on a flagged session, so conversion data and audiences stay clean.' },
];

const PROCESS = [
  { h: 'A visitor arrives from a paid source', p: 'Monitoring starts the moment someone lands on your client\'s site from Google, Meta or Microsoft Ads.' },
  { h: '2,000+ tests run in parallel', p: 'Geolocation, device type, click cadence, IP range behavior, network anomalies, session behavior. Evaluated simultaneously, in real time.' },
  { h: 'A verdict is assigned immediately', p: 'The signals accumulate into a fraud risk score. Once it crosses the detection threshold a block verdict fires automatically, with no manual review.' },
  { h: 'One verdict, every platform at once', p: 'The visitor is added to your Google Ads IP exclusion list and audience exclusion list, placed in a custom exclusion audience on Meta Ads, and excluded from Microsoft Ads targeting. All from one dashboard.' },
];

const SETUP_CHIPS = [
  { icon: '⚡', label: 'Quick installation' },
  { icon: 'W', label: 'Works with WordPress' },
  { icon: 'H', label: 'Works with HubSpot' },
  { icon: '✓', label: 'SOC 2 Type II' },
  { icon: '🔒', label: 'ISO 27001' },
  { icon: '🔒', label: 'ISO 27701' },
  { icon: '🌿', label: 'GDPR and CCPA' },
  { icon: 'G', label: 'Google Ads' },
  { icon: 'M', label: 'Meta Ads' },
  { icon: 'B', label: 'Microsoft Ads' },
];

const START_STEPS = [
  { h: 'Create your agency account', p: 'Sign up through the agency flow and start your 7-day trial.' },
  { h: 'Connect your clients', p: 'Each client with their own domain is connected under your partner account.' },
  { h: 'Partner pricing applies at billing', p: 'Billing is hands off — based on your qualification. The right discount is applied automatically.' },
  { h: 'Certify within 60 days', p: 'Short course and exam. Then the badge is yours.' },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ag-faq-item">
      <button className="ag-faq-q" onClick={() => setOpen(o => !o)}>
        {q}<span className={`ag-faq-plus ${open ? 'open' : ''}`}>+</span>
      </button>
      {open && <div className="ag-faq-a">{a}</div>}
    </div>
  );
}

export default function Agencies() {
  const [productOpen, setProductOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const productRef = useRef(null);
  const resourcesRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const close = e => {
      if (productRef.current && !productRef.current.contains(e.target)) setProductOpen(false);
      if (resourcesRef.current && !resourcesRef.current.contains(e.target)) setResourcesOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const tiers = [
    { name: 'Partner',   pillClass: 't1', pctClass: 't1', clients: '5 to 15 clients*' },
    { name: 'Preferred', pillClass: 't2', pctClass: 't2', clients: '16 to 29 clients*' },
    { name: 'Premier',   pillClass: 't3', pctClass: 't3', clients: '30 or more clients*' },
  ];

  return (
    <div className="ag-root">
      <Helmet>
        <title>Agencies — ClickSaver Partner Program</title>
        <meta name="description" content="Clean campaigns on every account you manage. Partner pricing up to 40% off — covers your whole book of business, no limited-time catches." />
      </Helmet>
      <style>{CSS}</style>

      {/* Nav */}
      <nav className="ag-nav">
        <div className="ag-nav-inner">
          <Link to="/" className="ag-logo">
            <span className="ag-logo-mark">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>
            </span>
            <span className="ag-logo-text">ClickSaver</span>
          </Link>
          <div className="ag-nav-links">
            <div
              className="ag-dropdown"
              ref={productRef}
              onMouseEnter={() => setProductOpen(true)}
              onMouseLeave={() => setProductOpen(false)}
            >
              <button className={`ag-dropdown-btn ${productOpen ? 'open' : ''}`} onClick={() => setProductOpen(o => !o)}>
                Product <span className={`ag-dropdown-arrow ${productOpen ? 'open' : ''}`}>▾</span>
              </button>
              {productOpen && (
                <div className="ag-mega">
                  <div className="ag-mega-grid">
                    {PRODUCT_ITEMS.map(p => (
                      <Link key={p.label} to={p.to} className="ag-mega-item" onClick={() => setProductOpen(false)}>
                        <span className="ag-mega-icon">{p.icon}</span>
                        <div>
                          <div className="ag-mega-label">
                            {p.label}
                            {p.badge && <span className="ag-mega-badge">{p.badge}</span>}
                          </div>
                          <p className="ag-mega-desc">{p.desc}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <Link to="/pricing" className="ag-nav-a">Pricing</Link>
            <Link to="/agencies" className="ag-nav-a" style={{ color: '#0969da', fontWeight: 600 }}>Agencies</Link>
            <div
              className="ag-dropdown"
              ref={resourcesRef}
              onMouseEnter={() => setResourcesOpen(true)}
              onMouseLeave={() => setResourcesOpen(false)}
            >
              <button className={`ag-dropdown-btn ${resourcesOpen ? 'open' : ''}`} onClick={() => setResourcesOpen(o => !o)}>
                Resources <span className={`ag-dropdown-arrow ${resourcesOpen ? 'open' : ''}`}>▾</span>
              </button>
              {resourcesOpen && (
                <div className="ag-mega">
                  <div className="ag-mega-grid">
                    {RESOURCE_ITEMS.map(r => (
                      <Link key={r.to} to={r.to} className="ag-mega-item" onClick={() => setResourcesOpen(false)}>
                        <span className="ag-mega-icon">{r.icon}</span>
                        <div>
                          <div className="ag-mega-label">{r.label}</div>
                          <p className="ag-mega-desc">{r.desc}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Link to="/login" className="ag-btn ag-btn-outline">Sign In</Link>
            <Link to="/register" className="ag-btn ag-btn-primary">Start Free Trial</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="ag-hero">
        <div className="ag-hero-inner">
          <div className="ag-pill-badge">ClickSaver Agency Partner Program</div>
          <h1 className="ag-h1">
            Clean campaigns. On <span className="accent">every account</span> you manage
          </h1>
          <p className="ag-hero-sub">
            Invalid traffic pollutes your clients' accounts whether or not the platform reports it. ClickSaver catches it, blocks the source, and keeps the data your optimisation runs on honest. One plan covers your whole book of business at partner pricing.
          </p>
          <Link to="/register" className="ag-btn ag-btn-orange" style={{ padding: '14px 32px', fontSize: 16 }}>Start free trial</Link>
          <div className="ag-trial-note">7-day free trial. 5 clients minimum to join.</div>
          <div className="ag-hero-trust">
            <div className="ag-trust-item"><span className="ag-trust-icon">⛉</span> 10K+ advertisers protected</div>
            <div className="ag-trust-item"><span className="ag-trust-icon">★</span> 1,000+ reviews</div>
            <div className="ag-trust-item"><span className="ag-trust-icon">✓</span> 2,000+ tests per visit</div>
            <div className="ag-trust-item"><span className="ag-trust-icon">◉</span> Google, Meta &amp; Microsoft Ads</div>
          </div>
        </div>
      </section>

      {/* What ad platforms report is not what's happening */}
      <section className="ag-section">
        <div className="ag-split">
          <div>
            <h2>What your ad platforms report, is <span className="accent">not what is really happening</span></h2>
            <p>You already see some of it. A line for invalid clicks in your ad platform reports that goes up. A session in analytics that lasted less than one second. A form fill with a real-looking email and a phone number that does not connect. A CPA that drifts up for no obvious reason.</p>
            <p>Those are the pieces that made it far enough to get counted. What you cannot see is the rest. Bot traffic and automation tools, malicious AI running inside a real browser. Click farms. Proxy and VPN networks cycling through addresses. Scrapers and crawlers that never touched an ad and still land in the analytics you report on. Form fills with a real-looking email attached to nothing.</p>
            <p>ClickSaver runs 2,000+ enterprise-level behavioural and technical tests on every visit and sorts what it finds into distinct threats. Invalid bot activity, suspicious activity, and malicious activity, per campaign and per platform. That is a breakdown you cannot get from the ad platforms or analytics alone.</p>
          </div>
          <div className="ag-threat-card">
            <h3 className="ag-threat-h">Detected threat groups &amp; types</h3>
            <div className="ag-donut">
              <svg width="160" height="160" viewBox="0 0 42 42">
                <circle cx="21" cy="21" r="15.915" fill="#fff" stroke="#f5f5f5" strokeWidth="6" />
                <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#d8bfa6" strokeWidth="6" strokeDasharray="51.7 48.3" strokeDashoffset="25" />
                <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#08a86b" strokeWidth="6" strokeDasharray="20.9 79.1" strokeDashoffset="-26.7" />
                <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#0969da" strokeWidth="6" strokeDasharray="26.9 73.1" strokeDashoffset="-47.6" />
                <circle cx="21" cy="21" r="15.915" fill="transparent" stroke="#fbbf24" strokeWidth="6" strokeDasharray="0.6 99.4" strokeDashoffset="-74.5" />
              </svg>
              <div className="ag-donut-overlay">
                <div className="ag-donut-overlay-label">Total threats</div>
                <div className="ag-donut-overlay-value">3,448 <span className="ag-donut-trend">↗ 15%</span></div>
              </div>
            </div>
            <div className="ag-threat-legend">
              <div className="ag-threat-legend-row"><span className="ag-threat-dot" style={{ background: '#d8bfa6' }} />Marketing optimisation rules <strong>1,781 (51.7%)</strong></div>
              <div className="ag-threat-legend-row"><span className="ag-threat-dot" style={{ background: '#08a86b' }} />Invalid bot activity <strong>721 (20.9%)</strong></div>
              <div className="ag-threat-legend-row"><span className="ag-threat-dot" style={{ background: '#0969da' }} />Invalid suspicious activity <strong>927 (26.9%)</strong></div>
              <div className="ag-threat-legend-row"><span className="ag-threat-dot" style={{ background: '#fbbf24' }} />Invalid malicious activity <strong>19 (0.6%)</strong></div>
            </div>
          </div>
        </div>
        <div className="ag-threat-callout">
          Campaign quality is your product. Invalid traffic degrades it quietly, and you end up defending numbers that were never yours to begin with.
        </div>
      </section>

      {/* What you get on every account */}
      <section className="ag-section">
        <h2 className="ag-section-h">What you get on every account</h2>
        <div className="ag-feat-grid">
          {FEATURES.map(f => (
            <div key={f.title} className="ag-feat">
              <div className="ag-feat-icon">{f.icon}</div>
              <h3 className="ag-feat-title">{f.title}</h3>
              <p className="ag-feat-body">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* From click to block */}
      <section className="ag-process">
        <div className="ag-section">
          <h2 className="ag-section-h">From <span className="accent-orange">click</span> to <span className="accent-pink">block</span></h2>
          <p className="ag-section-sub">Every visit from a paid source runs through this automatically, in real time.</p>
          <div className="ag-process-steps">
            {PROCESS.map((p, i) => (
              <div key={i} className="ag-step">
                <div className="ag-step-num">{i + 1}</div>
                <h3 className="ag-step-h">{p.h}</h3>
                <p className="ag-step-p">{p.p}</p>
              </div>
            ))}
          </div>
          <div className="ag-process-emphasis">
            That last step is the one that matters when you are running fifteen accounts. <span className="accent">You are not doing the same exclusion work three times per client.</span>
          </div>
        </div>
      </section>

      {/* Easy Setup */}
      <section className="ag-setup">
        <div className="ag-section">
          <div className="ag-setup-split">
            <div>
              <h2>Easy setup and quick wins for your customer</h2>
              <p>Installation is a script and a platform connection. If you already manage the site or the tag manager, it is an easy job — and it is the same job on every account after the first.</p>
              <p>When a client asks what exactly you are putting on their site, this is the answer. ClickSaver is built to the same compliance posture as the enterprise tools your IT reviewers already approved — the detection engine protects over 10,000 clients, from independent advertisers to global enterprise teams.</p>
              <div className="ag-pill-row">
                {SETUP_CHIPS.map((c, i) => (
                  <div key={i} className="ag-pill-chip">
                    <span className="ag-pill-chip-icon">{c.icon}</span>{c.label}
                  </div>
                ))}
              </div>
            </div>
            <div className="ag-code">
              <div className="ag-code-header">
                <span className="ag-code-dot" style={{ background: '#ff5f56' }} />
                <span className="ag-code-dot" style={{ background: '#ffbd2e' }} />
                <span className="ag-code-dot" style={{ background: '#27c93f' }} />
                <span className="ag-code-file">index.html — client site</span>
              </div>
              <div className="ag-code-body">
                <div><span className="ag-code-comment">{'<!-- ClickSaver protection -->'}</span></div>
                <div><span className="ag-code-tag">{'<script '}</span><span className="ag-code-attr">src</span>=<span className="ag-code-str">"https://api.clicksaver.app/monitor.js"</span><span className="ag-code-tag">{'>'}</span></div>
                <div><span className="ag-code-tag">{'</script>'}</span></div>
                <div className="ag-code-ok">✓ Connected · Google Ads, Meta Ads, Microsoft Ads</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="ag-section">
        <h2 className="ag-pricing-h">Priced so you can cover <span className="accent">your whole book of business</span></h2>
        <p className="ag-section-sub">Our partner rate card works not just for you but for your entire book of business. Smaller accounts or larger, under one ClickSaver partner account with the same discount.</p>

        <div className="ag-entry">
          <p className="ag-entry-text">
            The entry plan covers up to <strong>10 domains</strong> and <strong>20,000 protected visits</strong> a month. Partner rates are tailored to the size of your book of business — <Link to="/contact?topic=partner" style={{ color: '#0969da', textDecoration: 'none', fontWeight: 500 }}>talk to a partner manager</Link> to see your rate.
          </p>
        </div>

        <div className="ag-tiers">
          {tiers.map(t => (
            <div key={t.name} className="ag-tier">
              <span className={`ag-tier-pill ${t.pillClass}`}>{t.name}</span>
              <div className="ag-tier-sub">{t.clients}</div>
              <div className={`ag-tier-ask ${t.pctClass}`}>Custom partner pricing</div>
              <Link to="/contact?topic=partner" className={`ag-tier-cta ${t.pctClass}`}>Contact us to learn more →</Link>
            </div>
          ))}
        </div>

        <div className="ag-footnote">
          <span className="ag-footnote-dot">●</span>
          <span>Counted as clients based on specific domains, whether 1 or more per client. Recalculated monthly, with your tier applied automatically from the next invoice.</span>
        </div>

        <div className="ag-callout">
          <h3 className="ag-callout-h">This is not a limited-time promotion</h3>
          <p className="ag-callout-p">
            ClickSaver runs limited-time offers from time to time. Partner pricing replaces them — and it's valid for as long as you're in the program.
          </p>
        </div>
      </section>

      {/* Getting started */}
      <section className="ag-start">
        <div className="ag-section">
          <h2 className="ag-section-h">Getting started</h2>
          <div className="ag-start-steps">
            {START_STEPS.map((s, i) => (
              <div key={i} className="ag-start-card">
                <div className="ag-start-num">{i + 1}</div>
                <h3 className="ag-start-h">{s.h}</h3>
                <p className="ag-start-p">{s.p}</p>
              </div>
            ))}
          </div>
          <div className="ag-start-qualify">
            <h4 className="ag-start-qualify-h">How to qualify</h4>
            <p className="ag-start-qualify-p">
              Your agency needs at least <strong>5 distinct clients</strong> in your ClickSaver account. A client counts when it is a distinct business, has its own ad account under your manager account, and runs <strong>3,000+ protected visits per month</strong> on a 3-month average. Only new clients count toward your qualification.
            </p>
          </div>
        </div>
      </section>

      {/* Certified Partner */}
      <section className="ag-section ag-cert">
        <div className="ag-cert-split">
          <div>
            <h2>Certified ClickSaver Partner</h2>
            <p>Complete a short certification within 60 days of joining. It covers how to set up the account correctly, how the detection works, and how to understand the data. That last part is what you will use on every call where someone asks what they are paying for.</p>
            <p>Certifying earns you the partner badge and keeps your agency receiving the partner discount.</p>
            <p>Certification is required to stay in the program. If it is not completed inside the 60 days the partnership eligibility expires, but you are welcome to reapply.</p>
          </div>
          <div className="ag-cert-badge">
            <div className="ag-cert-ribbon">
              <svg width="38" height="38" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg>
            </div>
            <div className="ag-cert-badge-h">Certified ClickSaver Partner</div>
            <div className="ag-cert-badge-tier">Certified Partner</div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ag-faq">
        <div className="ag-section">
          <h2 className="ag-faq-h">Frequently asked questions</h2>
          <div className="ag-faq-list">
            {FAQ.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} />)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <div className="ag-cta">
        <h2 className="ag-cta-h">Ready to protect every client's budget?</h2>
        <p className="ag-cta-p">Talk to a partner manager and get set up with your partner account in under a day.</p>
        <div className="ag-cta-row">
          <Link to="/contact?topic=partner" className="ag-btn ag-btn-orange" style={{ padding: '13px 28px', fontSize: 15 }}>Apply to the Partner Program</Link>
          <Link to="/pricing" className="ag-btn ag-btn-outline" style={{ padding: '13px 28px', fontSize: 15 }}>See standard pricing</Link>
        </div>
      </div>
    </div>
  );
}
